import { useEffect } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';

import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { RHFCustomerSelect } from 'components/input/RHFCustomerSelect';
import { Grid } from 'components/layout';
import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { NormalizedLoad, TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';

import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';

export type CustomerSectionFormProps = {
  formMethods: UseFormReturn<NormalizedLoad>;
  customers: Maybe<TMSCustomer[]>;
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
  isLoadingCustomers: boolean;
  setIsLoadingCustomers: React.Dispatch<React.SetStateAction<boolean>>;
  /** Remount customer DebounceSelect after suggestion hydrate (see TurvoLoadForm). */
  customerSelectMountKey?: number;
  originalSuggestionData?: NormalizedLoad;
  aiHighlightedExternalTMSID?: string;
};

export function CustomerSectionForm({
  formMethods,
  customers,
  setCustomers,
  isLoadingCustomers,
  setIsLoadingCustomers,
  customerSelectMountKey = 0,
  originalSuggestionData,
  aiHighlightedExternalTMSID,
}: CustomerSectionFormProps): React.JSX.Element {
  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = formMethods;

  const { tmsIntegrations } = useServiceFeatures();
  // Only dirty autofill when AI defaults are active (clears AI-filled labels).
  // Load View has aiDefaultValues=false — hydrate must not yellow-highlight fields.
  const { aiDefaultValues } = useExtendedFormContext();

  const watchedCustomer = watch('customer');
  const watchedAddress2 = watch('customer.addressLine2');

  useEffect(() => {
    const formCustomerId = watchedCustomer?.externalTMSID;
    if (!formCustomerId) {
      return;
    }
    const formId = String(formCustomerId).trim();
    const selectedCustomer = customers?.find(
      (c) =>
        c.externalTMSID != null && String(c.externalTMSID).trim() === formId
    );
    if (!selectedCustomer) {
      return;
    }

    Object.entries(selectedCustomer).forEach(([key, value]) => {
      setValue(`customer.${key}` as FieldPath<NormalizedLoad>, value, {
        shouldDirty: aiDefaultValues,
      });
    });
  }, [watchedCustomer?.externalTMSID, customers, aiDefaultValues, setValue]);

  return (
    <>
      <RHFCustomerSelect
        key={customerSelectMountKey}
        required
        name={`customer.externalTMSID`}
        label='Name'
        control={control}
        errors={errors}
        customers={customers}
        setCustomers={setCustomers}
        isLoadingCustomers={isLoadingCustomers}
        setIsLoadingCustomers={setIsLoadingCustomers}
        tmsID={tmsIntegrations?.[0]?.id}
        aiHighlightedExternalTMSID={aiHighlightedExternalTMSID}
        preserveSearchedSelection
      />

      <LoadBuildingTextInput
        name='customer.addressLine1'
        label='Address Line 1'
        readOnly={true}
      />

      {!!watchedAddress2 && (
        <LoadBuildingTextInput
          name='customer.addressLine2'
          label='Address Line 2'
          readOnly={true}
        />
      )}
      <LoadBuildingTextInput
        name='customer.city'
        label='City'
        readOnly={true}
      />

      <Grid cols='2' gap='sm' className='w-full m-0'>
        <LoadBuildingTextInput
          name='customer.state'
          label='State'
          readOnly={true}
        />
        <LoadBuildingTextInput
          name='customer.zipCode'
          label='Zip Code'
          placeholder='12345'
          readOnly={true}
        />
      </Grid>

      <LoadRefPillInput
        name='customer.refNumber'
        label='Ref #/ BOL'
        control={control}
        originalSuggestionData={originalSuggestionData || getValues()}
        autoSelectFirstOption={false}
      />

      <LoadRefPillInput
        name='poNums'
        label='PO #'
        control={control}
        originalSuggestionData={originalSuggestionData}
        autoSelectFirstOption={false}
      />
    </>
  );
}
